/**
 * @file Password.js
 * @description Component for changing user password with password visibility toggle
 */
import React, { useEffect, useRef, useState } from 'react';
import Toast from '../ui/toasts/Toast';
import { changePassword } from '../../services/userService';
import { useAuthContext } from '../../store/AuthContext';
import PasswordInput from '../ui/PasswordInput';
import style from '../ui/modals/modals.module.scss';
import { hasSequentialChars } from '../../utils/utils';
import { getText } from '../../i18n';

const Password = ({
    onYes = null,
    onNo = null,
    onHide = null,
    isModal = false,
}) => {
    const { companyConfig } = useAuthContext();
    const footerRef = useRef(null);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
    const [errors, setErrors] = useState({});
    const [passwordRequirements, setPasswordRequirements] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
    });

    useEffect(() => {
        if (footerRef.current) {
            const buttons = footerRef.current.querySelectorAll('.btn');
            let maxWidth = 0;

            buttons.forEach(button => {
                maxWidth = Math.max(maxWidth, button.offsetWidth);
            });

            buttons.forEach(button => {
                button.style.width = `${maxWidth}px`;
            });
        }
    }, []);

    const validatePassword = password => {
        setPasswordRequirements({
            length: password.length >= companyConfig.application?.password_min_length && password.length <= companyConfig.application?.password_max_length,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /\d/.test(password),
            special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
            repeats: !/(.)\1{3}/.test(password),
            sequential: !hasSequentialChars(password),
            patterns: !/(.{1,2})\1\1/.test(password),
        });
    };

    const handleCurrentPasswordChange = event => {
        setCurrentPassword(event.target.value);
        if (errors.currentPassword) {
            setErrors({ ...errors, currentPassword: null });
        }
    };

    const handleNewPasswordChange = event => {
        const value = event.target.value;
        setNewPassword(value);
        validatePassword(value);

        if (errors.newPassword) {
            setErrors({ ...errors, newPassword: null });
        }
    };

    const handleNewPasswordConfirmChange = event => {
        const value = event.target.value;
        setNewPasswordConfirm(value);

        if (errors.newPasswordConfirm) {
            setErrors({ ...errors, newPasswordConfirm: null });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!currentPassword) {
            newErrors.currentPassword = 'Current password is required';
        }

        if (!newPassword) {
            newErrors.newPassword = 'New password is required';
        }

        if (newPassword !== newPasswordConfirm) {
            newErrors.newPasswordConfirm = 'Passwords do not match';
        }

        const unmetRequirements = Object.entries(passwordRequirements)
            .filter(([, met]) => !met)
            .map(([req]) => req);

        if (unmetRequirements.length > 0) {
            newErrors.newPassword = `Password does not meet the following character requirements: ${unmetRequirements.join(', ')}`;
        }

        return newErrors;
    };

    const renderPasswordRequirement = (requirement, text) => {
        const isMet = passwordRequirements[requirement];
        return (
            <small
                className={`password-requirement d-block ${!newPassword ? 'text-muted' : isMet ? 'text-success' : 'text-danger'}`}
            >
                {newPassword && (
                    <i className={`bi ${isMet ? 'bi-check' : 'bi-x'} me-2`}></i>
                )}
                {text}
            </small>
        );
    };

    const handleYes = async () => {
        const formErrors = validateForm();

        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            const errorMessages = Object.values(formErrors).join(', ');
            Toast.error(errorMessages);
            return;
        }

        try {
            let logThemOut = false;
            if (currentPassword && newPassword && newPasswordConfirm) {
                if (newPassword === newPasswordConfirm) {
                    const responseStatus = await changePassword(
                        currentPassword,
                        newPassword,
                    );
                    if (responseStatus === 200) {
                        logThemOut = true;
                    }
                }
            }
            onYes();
            onHide();
            if (logThemOut) {
                Toast.success(getText('msg_abd28e4ea76f'));
            }
        } catch (error) {
            if (error?.response?.data?.detail) {
                Toast.error(error.response.data.detail);
            } else {
                Toast.error(getText('msg_2f448d8fccc1'));
            }
        }
    };

    const handleNo = () => {
        onNo();
        onHide();
    };

    return (
        <div className={`modal-body ${style.modalBody}`}>
            <div className="mt-4">
                <div className={`${style.securityPasswordSeparator}`}>
                    <div style={{ width: 'fit-content' }}>{getText('msg_4d5967896006')}</div>
                    <div style={{ marginBottom: '16px' }}>
                        <PasswordInput
                            id="currentPassword"
                            name="currentPassword"
                            value={currentPassword}
                            onChange={handleCurrentPasswordChange}
                            placeholder={getText('msg_4d5967896006')}
                            className={`${style.securityPasswordFieldSeparator}`}
                            errorMessage={errors.currentPassword}
                        />
                    </div>

                    <div style={{ width: 'fit-content' }}>{getText('msg_4894cb39ee41')}</div>
                    <div style={{ marginBottom: '16px' }}>
                        <PasswordInput
                            id="newPassword"
                            name="newPassword"
                            value={newPassword}
                            onChange={handleNewPasswordChange}
                            placeholder={getText('msg_4894cb39ee41')}
                            className={`mb-1 ${style.securityPasswordFieldSeparator}`}
                            errorMessage={errors.newPassword}
                        />
                        <div
                            className="password-requirements mt-1"
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'start',
                                width: 'fit-content',
                            }}
                        >
                            {renderPasswordRequirement(
                                'length',
                                `Between ${companyConfig.application?.password_min_length}-${companyConfig.application?.password_max_length} characters`
                            )}
                            {renderPasswordRequirement(
                                'uppercase',
                                'Contains at least 1 uppercase letter',
                            )}
                            {renderPasswordRequirement(
                                'lowercase',
                                'Contains at least 1 lowercase letter',
                            )}
                            {renderPasswordRequirement(
                                'number',
                                'Contains at least 1 number',
                            )}
                            {renderPasswordRequirement(
                                'special',
                                'Contains at least 1 special character',
                            )}
                            {companyConfig.application?.password_allow_duplicate_values || renderPasswordRequirement(
                                'repeats',
                                'No 4 consecutive repeating characters',
                            )}
                            {companyConfig.application?.password_allow_duplicate_values || renderPasswordRequirement(
                                'sequential',
                                'No 4 sequential characters',
                            )}
                            {companyConfig.application?.password_allow_duplicate_values || renderPasswordRequirement(
                                'patterns',
                                'No repeating patterns',
                            )}
                        </div>
                    </div>

                    <div className="mt-3" style={{ width: 'fit-content' }}>
                        {getText('msg_68dcd71ce6ba')}
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                        <PasswordInput
                            id="newPasswordConfirm"
                            name="newPasswordConfirm"
                            value={newPasswordConfirm}
                            onChange={handleNewPasswordConfirmChange}
                            placeholder={getText('msg_68dcd71ce6ba')}
                            errorMessage={errors.newPasswordConfirm}
                            isConfirmField={true}
                            originalPassword={newPassword}
                            showMatchFeedback={true}
                        />
                    </div>
                </div>
            </div>
            <div
                className={`d-flex justify-content-evenly mt-5`}
                ref={footerRef}
            >
                {isModal && (
                    <button
                        type="button"
                        className="ing-popup-blue-negative"
                        onClick={handleNo}
                    >
                        {getText('msg_77dfd2135f4d')}
                    </button>
                )}
                <button
                    type="button"
                    className="ing-button-primary-none"
                    onClick={handleYes}
                >
                    {getText('msg_efc007a393f6')}
                </button>
            </div>
        </div>
    );
};

export default Password;
